import Global from "../../../components/Atomos/Icons/Global";
import LocationPin from "../../../components/Atomos/Icons/LocationPin";
import type { NetworkingCategoryKey } from "./networkingMapCategories";
import NetworkingMapFilters from "./NetworkingMapFilters";

type NetworkingMapHeaderPanelProps = {
  name: string;
  avatar?: string;
  jurisdictionLabel?: string;
  location?: string;
  matchCount: number;
  selectedCategories: NetworkingCategoryKey[];
  onToggleCategory: (category: NetworkingCategoryKey, checked: boolean) => void;
  filtersDisabled?: boolean;
  isLoading?: boolean;
};

export default function NetworkingMapHeaderPanel({
  name,
  avatar,
  jurisdictionLabel,
  location,
  matchCount,
  selectedCategories,
  onToggleCategory,
  filtersDisabled = false,
  isLoading = false,
}: NetworkingMapHeaderPanelProps) {
  return (
    <section className="flex w-full flex-col items-start gap-6 self-stretch rounded-lg bg-[#F6F6F6] p-4 lg:grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-start">
      <div className="flex min-w-0 gap-4 sm:gap-5">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full bg-gray-200 sm:h-24 sm:w-24">
          {isLoading ? (
            <div className="h-full w-full animate-pulse bg-[#D6D6D6]" aria-hidden />
          ) : avatar ? (
            <img
              src={avatar}
              alt=""
              className="h-full w-full object-cover"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[#4A5568] font-['Roboto'] text-xl font-semibold text-white">
              {name
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part[0]?.toUpperCase() ?? "")
                .join("")}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          {isLoading ? (
            <div className="space-y-3" aria-hidden>
              <div className="h-5 w-40 max-w-full animate-pulse rounded bg-[#D6D6D6]" />
              <div className="h-4 w-56 max-w-full animate-pulse rounded bg-[#D6D6D6]" />
              <div className="h-8 w-52 max-w-full animate-pulse rounded-lg bg-[#D6D6D6]" />
            </div>
          ) : (
            <>
              <p className="font-['Roboto'] text-base font-semibold leading-normal text-[#474F56]">
                {name}
              </p>

              {(jurisdictionLabel || location) && (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                  {jurisdictionLabel && (
                    <span className="flex min-w-0 items-center gap-1 font-['Roboto'] text-base font-normal leading-5 text-[#474F56]">
                      <Global className="h-4 w-4 shrink-0 text-primary" />
                      <span className="min-w-0 break-words">
                        Jurisdiction: {jurisdictionLabel}
                      </span>
                    </span>
                  )}
                  {location && (
                    <span className="flex min-w-0 items-center gap-1 font-['Roboto'] text-base font-normal leading-5 text-[#474F56]">
                      <LocationPin className="h-4 w-4 shrink-0 text-primary" />
                      <span className="min-w-0 break-words">{location}</span>
                    </span>
                  )}
                </div>
              )}

              <p className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-[#D6D6D6] px-4 py-1 font-['Roboto'] text-base font-medium leading-5 text-[#474F56]">
                Showing {matchCount} matches in this event
              </p>
            </>
          )}
        </div>
      </div>

      <NetworkingMapFilters
        selected={selectedCategories}
        onToggle={onToggleCategory}
        disabled={filtersDisabled}
        className="min-w-0 w-full"
      />
    </section>
  );
}
